// src/services/predictionService.ts
import axiosInstance from '../config/axiosInstance';
import { GlobalForecastSummary, ViewMode } from '../types/predictions';

// --- INTERFAZ TRINITY (Backend Contract V8) ---
// Define exactamente lo que devuelve la API prediction_api.py
interface TrinityGlobalRow {
  prediction_date: string;
  
  // Datos Reales
  total_revenue: number;      // Estrategia (Projected)
  total_quantity: number;     // Estrategia (Simulated Vol)
  
  revenue_base: number;       // Inercia (BHS)
  quantity_base: number;      // Inercia (Base Forecast)
  
  revenue_potential: number;  // Techo IA (Max AI)
  quantity_potential: number; // Techo IA Vol
  
  value: number;              // Alias Legacy
}

interface BackendGlobalResponse {
  data: TrinityGlobalRow[];
  tier_limit_applied: string;
}

// --- INTERFAZ BREAKDOWN ---
export interface ScenarioRow {
  entity_id: string;
  entity_name: string;
  base_quantity: number;
  simulated_quantity: number;
  revenue_bhs: number;
  revenue_projected: number;
  delta_revenue: number;
  delta_quantity: number;
  confidence_level: 'HIGH' | 'MEDIUM' | 'LOW';
}

// --- INTERFAZ UI ---
export interface GlobalForecastResponse {
  data: GlobalForecastSummary[];
  tier_limit_applied: string;
}

/**
 * [V8.3 - TRINITY SYNC] 
 * Obtiene la tendencia maestra DIRECTA del Backend sin heurísticas falsas.
 * Mapea 1:1 los datos de simulación reales.
 */
export const getGlobalForecast = async (): Promise<GlobalForecastResponse> => {
  try {
    const response = await axiosInstance.get<BackendGlobalResponse>('/predictions/global', {
      params: { period: 'next_12_months' }
    });

    const { data: rows, tier_limit_applied } = response.data;

    const processedData: GlobalForecastSummary[] = (rows || []).map((row) => {
      // MAPEO DIRECTO: La Verdad del Backend sin intermediarios
      return {
        prediction_date: row.prediction_date,
        
        // KPI 1: Estrategia (Projected)
        total_revenue: row.total_revenue,
        total_quantity: row.total_quantity,
        
        // KPI 2: Base (BHS)
        revenue_base: row.revenue_base,
        quantity_base: row.quantity_base,
        
        // KPI 3: Potencial (Max AI)
        revenue_potential: row.revenue_potential,
        quantity_potential: row.quantity_potential,
        
        total_margin: 0 // Placeholder si no se usa en gráfico
      };
    });

    // Ordenar cronológicamente por seguridad
    processedData.sort((a, b) => 
      new Date(a.prediction_date).getTime() - new Date(b.prediction_date).getTime()
    );

    return {
      data: processedData,
      tier_limit_applied: tier_limit_applied || ""
    };

  } catch (error) {
    console.error("[PredictionService] Error fatal obteniendo forecast global:", error);
    throw error;
  }
};

/**
 * [V8.0 - TRINITY PARENTS] 
 */
export const getForecastBreakdown = async (
  month: string, 
  viewMode: ViewMode
): Promise<ScenarioRow[]> => {
  try {
    const granularity = viewMode === 'products' ? 'product_id' : 'customer_id';
    const response = await axiosInstance.get<ScenarioRow[]>('/predictions/breakdown', {
      params: {
        prediction_date: month,
        granularity_level: granularity
      }
    });
    return response.data || [];
  } catch (error) {
    console.error("[PredictionService] Error breakdown (Parents):", error);
    return [];
  }
};

/**
 * [V8.0 - TRINITY CHILDREN]
 */
export const getPredictionBreakdownChildren = async (
  month: string,
  childGranularity: string, 
  parentId: string
): Promise<ScenarioRow[]> => {
  try {
    const apiContextGranularity = childGranularity === 'customer_id' ? 'product_id' : 'customer_id';
    const response = await axiosInstance.get<ScenarioRow[]>('/predictions/breakdown', {
      params: {
        prediction_date: month,
        granularity_level: apiContextGranularity, 
        parent_entity_id: parentId                
      }
    });
    return response.data || [];
  } catch (error) {
    console.error("[PredictionService] Error breakdown (Children):", error);
    return [];
  }
};